import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { groupTopicsByTheme, filterTopicsBySearch, getTopicDisplayName } from "../../utils/topicUtils";
import { PostCodes } from "../../api/APISurvey";
import { useSnackbar } from "../../Content";
import { getUniqueKey } from "../../Formats";
import "./TopicSelector.scss";

const TopicSelector = ({
    categoryData,
    selectedTopics = [],
    onSelect,
    onRefresh,
    title,
    searchPlaceholder,
    isTopicShared,
}) => {
    const intl = useIntl();
    const [searchText, setSearchText] = useState("");

    const themes = useMemo(
        () => groupTopicsByTheme(categoryData, intl),
        [categoryData, intl]
    );

    const filteredThemes = useMemo(
        () => filterTopicsBySearch(themes, searchText, intl),
        [themes, searchText, intl]
    );

    const hasSearch = !!searchText;

    return (
        <div className="topic-selector">
            {title && <h1>{title}</h1>}
            <input
                type="text"
                className="search"
                value={searchText}
                autoFocus
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={
                    searchPlaceholder ||
                    intl.formatMessage({
                        id: "codes.search.placeholder",
                        defaultMessage: "Search Support Topics",
                    })
                }
            />
            <div className="accordion-container">
                {Object.entries(filteredThemes).sort(([a], [b]) => {
                    const aIsOther = a === '99' || a === 'undefined';
                    const bIsOther = b === '99' || b === 'undefined';
                    if (aIsOther && !bIsOther) return 1;
                    if (!aIsOther && bIsOther) return -1;
                    return 0;
                }).map(([themeKey, group]) => (
                    <ThemeAccordion
                        key={themeKey}
                        themeKey={themeKey}
                        group={group}
                        selectedTopics={selectedTopics}
                        onSelect={onSelect}
                        onRefresh={onRefresh}
                        isOpen={hasSearch}
                        isSearching={hasSearch}
                        isTopicShared={isTopicShared}
                    />
                ))}
                {hasSearch && Object.keys(filteredThemes).length === 0 && (
                    <div className={"no-results"}>
                        {intl.formatMessage({id:'noResults', defaultMessage:'No results with given search term'})}
                    </div>
                )}
            </div>
        </div>
    );
};

const ThemeAccordion = ({
    themeKey,
    group,
    selectedTopics,
    onSelect,
    onRefresh,
    isOpen: defaultIsOpen,
    isSearching,
    isTopicShared,
}) => {
    const [isOpen, setIsOpen] = useState(defaultIsOpen || false);
    const intl = useIntl();

    React.useEffect(() => {
        if (defaultIsOpen !== undefined) {
            setIsOpen(defaultIsOpen);
            setCustomOpen(defaultIsOpen);
        }
    }, [defaultIsOpen]);

    const hasCustom = group.custom.length > 0;
    const [customOpen, setCustomOpen] = useState(false);

    return (
        <div className={`${isOpen ? "open" : ""} accordion`}>
            <h2 onClick={() => setIsOpen(!isOpen)}>
                <span className="toggle">{isOpen ? "-" : "+"}</span>
                {group.name}
            </h2>
            {isOpen && (
                <div className="contents">
                    {group.taxonomy.map((item) => (
                        <TopicItem
                            key={item.code}
                            item={item}
                            isSelected={selectedTopics.includes(item.code)}
                            isShared={isTopicShared?.(item.code)}
                            onSelect={onSelect}
                        />
                    ))}
                    {hasCustom && (
                        <div
                            className={`custom-separator ${customOpen ? "open" : ""}`}
                            onClick={() => !isSearching && setCustomOpen(!customOpen)}
                        >
                            {!isSearching && <span className="toggle">{customOpen ? "-" : "+"}</span>}
                            <span>
                                {isSearching
                                    ? intl.formatMessage({
                                        id: "topic.customTopics",
                                        defaultMessage: "Custom topics",
                                    })
                                    : intl.formatMessage({
                                        id: "topic.customTopicsOrCreate",
                                        defaultMessage: "Custom topics ({count}) / Add new topic",
                                    }, { count: group.custom.length })
                                }
                            </span>
                        </div>
                    )}
                    {!isSearching && !hasCustom && (
                        <div
                            className={`custom-separator ${customOpen ? "open" : ""}`}
                            onClick={() => setCustomOpen(!customOpen)}
                        >
                            <span className="toggle">{customOpen ? "-" : "+"}</span>
                            <span>
                                {intl.formatMessage({
                                    id: "topic.addNewTopic",
                                    defaultMessage: "Add new topic",
                                })}
                            </span>
                        </div>
                    )}
                    {(isSearching || customOpen) && (
                        <>
                            {group.custom.map((item) => (
                                <TopicItem
                                    key={item.code}
                                    item={item}
                                    isSelected={selectedTopics.includes(item.code)}
                                    isShared={isTopicShared?.(item.code)}
                                    onSelect={onSelect}
                                />
                            ))}
                            {!isSearching && (
                                <AddNewTopic
                                    theme={themeKey}
                                    onSelect={onSelect}
                                    onRefresh={onRefresh}
                                />
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const TopicItem = ({ item, isSelected, isShared, onSelect }) => {
    const intl = useIntl();

    return (
        <div
            className={`item ${isSelected ? "in-use" : ""} ${item.data.taxonomy ? "taxonomy" : "custom"}`}
            onClick={() => onSelect(item.code, item.data)}
            title={
                isSelected
                    ? intl.formatMessage({
                          id: "supportTopicInUse",
                          defaultMessage: "Support topic is in use",
                      })
                    : ""
            }
        >
            {item.displayName}
        </div>
    );
};

const AddNewTopic = ({ theme, onSelect, onRefresh }) => {
    const intl = useIntl();
    const [showInput, setShowInput] = useState(false);
    const [title, setTitle] = useState("");
    const postCode = PostCodes();
    const { setSnackbarData } = useSnackbar();

    const handleSubmit = (e) => {
        e.preventDefault();
        if (title.trim().length < 1) return;

        const newKey = `TOPIC_${getUniqueKey()}`;
        const newCodeValue = {
            theme: theme,
            title: title.trim(),
        };
        const codeToPost = [
            {
                codeset: "category",
                code: newKey,
                value: newCodeValue,
            },
        ];

        postCode.mutate(codeToPost, {
            onError: (error) => {
                console.log(error);
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({
                        id: "addNewTopic.error",
                        defaultMessage:
                            "Adding a new topic failed. Please try again.",
                    }),
                    state: "error",
                    autoHide: true,
                });
            },
            onSuccess: () => {
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({
                        id: "addNewTopic.success",
                        defaultMessage: "New topic added",
                    }),
                    state: "success",
                    autoHide: true,
                });
                onSelect(newKey, newCodeValue);
                if (onRefresh) onRefresh();
                setTitle("");
                setShowInput(false);
            },
        });
    };

    if (showInput) {
        return (
            <form className="category-input" onSubmit={handleSubmit}>
                <input
                    type="text"
                    autoFocus
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={intl.formatMessage({
                        id: "survey.supportNeeds.newSupportNeedPlaceholder",
                        defaultMessage:
                            "Give your support need a descriptive name",
                    })}
                />
                <input
                    type="submit"
                    value={intl.formatMessage({
                        id: "addTopic",
                        defaultMessage: "+ Add Topic",
                    })}
                    disabled={title.trim().length < 1}
                />
            </form>
        );
    }

    return (
        <div className="item add-new" onClick={() => setShowInput(true)}>
            {intl.formatMessage({
                id: "addTopic",
                defaultMessage: "+ Add Topic",
            })}
        </div>
    );
};

export default TopicSelector;
export { getTopicDisplayName };
